import type { Topic } from './topics';

/**
 * The small language model as a classifier: what was asked, the lettered options and what the
 * child said go in one prompt, and the model's next-token scores for the option letters (and Z,
 * "none of these") pick the answer in a single forward pass. Nothing the model writes reaches the
 * child. The same prompt trains the model (ml/experiments/train_lab_slm.py reads the prompts this
 * file builds, via lab-prompts.json), so the two can never drift apart.
 */

/** Option letters (one token each in the model's vocabulary), and the letter for "none". */
export const LETTERS: readonly string[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];
export const NONE = 'Z';

export interface Message {
  role: 'system' | 'user';
  content: string;
}

/** The prompt: which option did the child mean, given the question and their words? */
export function choiceMessages(
  question: string,
  options: readonly string[],
  text: string,
): Message[] {
  if (options.length > LETTERS.length) throw new Error('too many options');
  const list = options.map((o, i) => `${LETTERS[i] ?? '?'}. ${o}`).join('\n');
  return [
    { role: 'system', content: 'Sort what a child said. Reply with one letter.' },
    {
      role: 'user',
      content: `Question: ${question}\n${list}\n${NONE}. none of these\nThe child said: "${text}"`,
    },
  ];
}

/** The question for sorting what a child asks the helper into the approved topics. */
export const TOPIC_QUESTION = 'What is the child asking about?';

export function topicMessages(text: string, topics: readonly Topic[]): Message[] {
  return choiceMessages(
    TOPIC_QUESTION,
    topics.map((t) => t.about),
    text,
  );
}

/**
 * The option (0-based) from the scores of the option letters followed by Z, or null for "none";
 * `p` is the chosen letter's probability among them (softmax).
 */
export function pickOption(letterLogits: readonly number[]): { index: number | null; p: number } {
  const max = Math.max(...letterLogits);
  const exp = letterLogits.map((l) => Math.exp(l - max));
  const sum = exp.reduce((a, b) => a + b, 0);
  let best = 0;
  for (let i = 1; i < exp.length; i++) if ((exp[i] ?? 0) > (exp[best] ?? 0)) best = i;
  const p = (exp[best] ?? 0) / sum;
  return { index: best === exp.length - 1 ? null : best, p };
}
